import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { Hand, HelpCircle, Mouse, MousePointerClick, X } from 'lucide-react'
import React from 'react'
import styled, { keyframes } from 'styled-components'

const fadeInMini = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.625rem);
  }
  to {
    opacity: 0.95;
    transform: translateY(0);
  }
`

const MiniInstructionsOverlay = styled.div`
  position: absolute;
  bottom: 0.5rem;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  padding: 0.75rem;
  border-radius: 0.5rem;
  color: white;
  z-index: 2000;
  animation: ${fadeInMini} 0.3s ease-out forwards;
  font-size: 0.75rem;
  opacity: 0.95;
  transition: opacity 0.2s ease;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: default;
  min-width: 16rem;
`

const ModeSpecificOverlay = styled(MiniInstructionsOverlay)`
  bottom: 12rem;
`

const ControlSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
`

const ControlLabel = styled.div`
  font-size: 0.6875rem;
  opacity: 0.95;
  text-transform: uppercase;
  letter-spacing: 0.05rem;
  margin-bottom: 0.125rem;
`

const MouseIconWrapper = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.8);
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`

const InstructionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
`

const MouseActionLabel = styled.span`
  font-weight: normal;
  opacity: 0.9;
  b {
    font-weight: 600;
    color: rgba(255, 255, 255, 1);
  }
`

const HudContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const BottomHelperTextStack = styled.div`
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: absolute;
  bottom: 0.5rem;
  right: 1rem;
  height: max-content;
`

const HelpIconButton = styled.button`
  position: absolute;
  bottom: 0.5rem;
  right: 1rem;
  width: 3rem;
  height: 3rem;
  background: transparent;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 500;
  pointer-events: auto;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.9);
  }

  svg {
    width: 1.5rem;
    height: 1.5rem;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8));
  }
`

const CloseIconButton = styled(HelpIconButton)`
  position: relative;
  bottom: auto;
  right: auto;
  margin-top: 0.25rem;
  margin-left: auto;
`

export const HelperText: React.FC = () => {
  const {
    selectedMode,
    measurementType,
    helperTextVisible,
    setHelperTextVisible,
  } = useKestrelViewerContext()

  const handleToggleHelperText = () => {
    setHelperTextVisible(!helperTextVisible)
  }

  return (
    <>
      {!helperTextVisible ? (
        <HelpIconButton onClick={handleToggleHelperText} title='Show help'>
          <HelpCircle />
        </HelpIconButton>
      ) : (
        <BottomHelperTextStack>
          <HudContainer>
            {(selectedMode === 'polygon' || selectedMode === 'surfaceArea') && (
              <ModeSpecificOverlay>
                <ControlLabel>Draw</ControlLabel>
                <ControlSection>
                  <InstructionRow>
                    <MouseActionLabel>
                      <b>Right click</b> to place vertices
                    </MouseActionLabel>
                  </InstructionRow>
                  <InstructionRow>
                    <MouseActionLabel>
                      <b>Connect</b> or <b>Enter</b> to complete
                    </MouseActionLabel>
                  </InstructionRow>
                  <InstructionRow>
                    <MouseActionLabel>
                      <b>Esc</b> to cancel drawing
                    </MouseActionLabel>
                  </InstructionRow>
                </ControlSection>
              </ModeSpecificOverlay>
            )}
            {selectedMode === 'cursor' && (
              <ModeSpecificOverlay>
                <ControlSection>
                  <ControlLabel>Annotate</ControlLabel>
                  <InstructionRow>
                    <MouseActionLabel>
                      <b>Right click</b> to Annotate
                    </MouseActionLabel>
                  </InstructionRow>
                </ControlSection>
              </ModeSpecificOverlay>
            )}
            {selectedMode === 'measure' && (
              <ModeSpecificOverlay>
                <ControlSection>
                  <ControlLabel>Measure</ControlLabel>
                  <InstructionRow>
                    {(measurementType === 'straightLine' ||
                      measurementType === 'surfaceFollowing') && (
                      <MouseActionLabel>
                        <b>U</b> to toggle straight-line and surface-following
                      </MouseActionLabel>
                    )}
                    {measurementType === 'thickness' && (
                      <MouseActionLabel>
                        <b>B</b> to cycle surface layer hits in thickness mode
                      </MouseActionLabel>
                    )}
                  </InstructionRow>
                  <InstructionRow>
                    <MouseActionLabel>
                      <b>Right click</b> to place measure point
                    </MouseActionLabel>
                  </InstructionRow>
                  <InstructionRow>
                    <MouseActionLabel>
                      <b>Press enter</b> to finalize measurement
                    </MouseActionLabel>
                  </InstructionRow>
                </ControlSection>
              </ModeSpecificOverlay>
            )}
            <MiniInstructionsOverlay>
              <ControlSection>
                <ControlLabel>Navigation</ControlLabel>
                <InstructionRow>
                  <MouseIconWrapper>
                    <Mouse />
                  </MouseIconWrapper>
                  <MouseActionLabel>
                    Hold <b>Left Mouse + Drag</b> to orbit
                  </MouseActionLabel>
                </InstructionRow>
                <InstructionRow>
                  <MouseIconWrapper>
                    <Hand />
                  </MouseIconWrapper>
                  <MouseActionLabel>
                    <b>Shift + Left Mouse + Drag</b> to pan
                  </MouseActionLabel>
                </InstructionRow>
                <InstructionRow>
                  <MouseIconWrapper>
                    <MousePointerClick />
                  </MouseIconWrapper>
                  <MouseActionLabel>
                    <b>Double Click</b> to focus on point
                  </MouseActionLabel>
                </InstructionRow>
                <InstructionRow>
                  <MouseIconWrapper>
                    <Mouse />
                  </MouseIconWrapper>
                  <MouseActionLabel>
                    <b>Scroll Wheel</b> or <b>Right Mouse + Drag</b> to zoom
                  </MouseActionLabel>
                </InstructionRow>
              </ControlSection>
            </MiniInstructionsOverlay>
          </HudContainer>
          <CloseIconButton onClick={handleToggleHelperText} title='Hide help'>
            <X />
          </CloseIconButton>
        </BottomHelperTextStack>
      )}
    </>
  )
}
